import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, CalendarCheck, ShieldCheck } from "lucide-react";
import { LogoFull } from "./Logo";
import { scrollToId } from "@/lib/api";

const links = [
  { label: "Services", id: "services" },
  { label: "Estimator", id: "calculator" },
  { label: "Reviews", id: "reviews" },
  { label: "About", id: "about" },
];

export const Navbar = ({ onBook }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${
        scrolled ? "bg-sand/85 backdrop-blur-xl shadow-[0_1px_0_0_#E5E1D5]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="navbar-brand-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <LogoFull plaque={scrolled} />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`navbar-${l.id}-link`}
              onClick={() => scrollToId(l.id)}
              className={`font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 hover:text-terra ${
                scrolled ? "text-obsidian/70" : "text-sand/70"
              }`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="tel:3059868602"
            data-testid="navbar-phone-link"
            className={`hidden md:flex items-center gap-2 font-mono text-xs tracking-wider transition-colors ${
              scrolled ? "text-obsidian" : "text-sand"
            }`}
          >
            <Phone size={14} className="text-terra" /> (305) 986-8602
          </a>
          <Link
            to="/admin"
            data-testid="navbar-admin-link"
            className={`hidden sm:flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase px-3 py-2 rounded-full border transition-colors ${
              scrolled ? "border-sandline text-obsidian/60 hover:text-terra" : "border-sand/20 text-sand/60 hover:text-sand"
            }`}
          >
            <ShieldCheck size={12} /> Admin
          </Link>
          <button
            data-testid="navbar-schedule-cta"
            onClick={() => onBook()}
            className="group flex items-center gap-2 bg-sun text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            <CalendarCheck size={15} className="transition-transform duration-300 group-hover:-rotate-12" />
            Book Inspection
          </button>
        </div>
      </div>
    </motion.header>
  );
};
